"use strict";

import "flowbite";

import Axios from "axios";
import Flowbite from "flowbite/dist/flowbite.js";
import Selectr from "mobius1-selectr";
import Swal from "sweetalert2";
import Toasted from "toastedjs";

import { Convalidation } from "./other/convalidations.js";
import { Elimination } from "./other/eliminations.js";
import { Generality } from "./other/generalities.js";
import { HttpError } from "./other/httpErrors.js";
import { Notification } from "./other/notifications.js";
import { Payment } from "./other/payments.js";
import { Region } from "./other/regions.js";
import { Selection } from "./other/selections.js";
import { Tool } from "./other/tools.js";

/**
 * Inicializa las bibliotecas necesarias y las expone en el objeto global.
 * Esto permite el uso global de estas bibliotecas en la aplicación.
 */
function initializeLibraries() {
    window.axios = Axios;
    window.Flowbite = Flowbite;
    window.Selectr = Selectr;
    window.Swal = Swal;
    window.Toasted = Toasted;
}

/**
 * Inicializa las instancias de las clases necesarias para la aplicación.
 * @returns {Object} Un objeto que contiene instancias de las clases de la aplicación.
 */
function initializeObjects() {
    const notification = new Notification();
    const httpError = new HttpError(notification);
    const tool = new Tool(httpError);

    return {
        convalidation: new Convalidation(notification, httpError, tool),
        elimination: new Elimination(notification, httpError),
        generality: new Generality(notification, httpError, tool),
        payment: new Payment(notification, httpError),
        region: new Region(httpError, tool),
        selection: new Selection(notification, httpError, tool),
    };
}

/**
 * Configura los oyentes de eventos necesarios para el comportamiento de la aplicación.
 * Se asegura de que los cambios en la interfaz y las interacciones estén bien gestionadas.
 * @param {Object} objects - Objeto que contiene las instancias de las clases de la aplicación.
 */
function setupEventListeners(objects) {
    /** Al cargar la pagina */
    document.addEventListener("DOMContentLoaded", () => {
        /** Métodos relacionados a la convalidación */
        objects.convalidation.changeUser();
        objects.convalidation.recognitionOnCreate();
        objects.convalidation.recognitionOnEdit();

        /** Métodos para eliminar */
        objects.elimination.destroyAcademicTitle();
        objects.elimination.destroyApplication();
        objects.elimination.destroyDegree();
        objects.elimination.destroyGender();
        objects.elimination.destroyIdentificationType();
        objects.elimination.destroyInstitution();
        objects.elimination.destroyJob();
        objects.elimination.destroyLabor();
        objects.elimination.destroyPayment();
        objects.elimination.destroyRecords();
        objects.elimination.destroyRecordsAdmin();
        objects.elimination.destroyStudy();
        objects.elimination.destroyUser();

        /** Métodos generales */
        objects.generality.disableForm();
        objects.generality.goUp();
        objects.generality.resendApplication();
        objects.generality.selectConfig();
        objects.generality.sendApplication();

        /** Métodos para gestionar los pagos */
        objects.payment.chooseMethod();

        /** Métodos para selección de datos desde un select */
        objects.selection.chooseAcademicTitle();
        objects.selection.chooseApplications();
        objects.selection.chooseInstitution();
        objects.selection.chooseProcedure();
        objects.selection.chooseRoleOnCreate();
        objects.selection.chooseRoleOnEdit();
        objects.selection.chooseStage();
        objects.selection.chooseStageCard();

        /** Métodos para selección de regiones */
        objects.region.chooseCountry();
        objects.region.chooseCountryThree();
        objects.region.chooseCountryTwo();
    });
}

/**
 * Configura Axios para manejar solicitudes HTTP, incluyendo la configuración del token CSRF.
 * Esto garantiza que las solicitudes AJAX sean seguras y se puedan realizar correctamente.
 */
function setupAxios() {
    window.axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

    const token = document.head.querySelector('meta[name="csrf-token"]');

    if (token) {
        window.axios.defaults.headers.common["X-CSRF-TOKEN"] = token.content;
    } else {
        console.error(
            "CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token"
        );
    }
}

/** Inicialización */
initializeLibraries();
const objects = initializeObjects();
setupEventListeners(objects);
setupAxios();
